import type { Verification } from "@sitescene/types";
import type { ReactNode } from "react";

import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";

import { AnchoredObservations } from "@/components/anchored-observations";
import { Badge } from "@/components/badge";
import { ClipPanel } from "@/components/clip-panel";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { Button, Field, Select, TextArea } from "@/components/field";
import { JsonBlock } from "@/components/json-block";
import { StatusMessage } from "@/components/status-message";
import {
	reanalysisEventPath,
	ROUTES,
	sceneProposalPath,
} from "@/constants/routes";
import { useTaxonomy } from "@/features/plausibility/use-taxonomy";
import {
	useRecordVerdict,
	useSuggestVerdictClass,
	useVerification,
	useVerificationVideo,
	type VerdictSuggestion,
} from "@/features/plausibility/use-verifications";
import { verifierCall } from "@/features/plausibility/verifier-call";
import {
	isNotFoundError,
	useFindEventByMeasurement,
} from "@/features/reanalysis/use-reanalysis";
import {
	apiErrorMessage,
	useCreateSceneProposal,
} from "@/features/scene-facts/use-scene-proposals";
import { refId } from "@/utils/ids";

import {
	formatEvidenceFactor,
	formatLogOdds,
	formatPercent,
	formatProbability,
	formatSeverityChange,
	formatWhen,
	probabilityFromLogOdds,
} from "./format";

/**
 * One verification, laid out as the chain of reasoning: what Stage 1 said,
 * what class that became, which prior applied and why, what each feature
 * contributed, what the blind pass thought, and where the numbers landed.
 * Raw model outputs are at the bottom, collapsed.
 */
export const VerificationDetail = () => {
	const { verificationId } = useParams();
	const { data, error, isLoading } = useVerification(verificationId);

	if (isLoading || error || !data) {
		return (
			<StatusMessage
				empty="Verification not found."
				error={error}
				isEmpty={!isLoading && !data}
				isLoading={isLoading}
				loading="Loading verification…"
			/>
		);
	}

	return (
		<div className="flex flex-col gap-4">
			<div className="flex flex-wrap items-center justify-between gap-3">
				<Link
					className="text-secondary text-sm underline"
					to={ROUTES.ORGANIZATION_VERIFICATIONS}
				>
					← All verifications
				</Link>
				<ReanalyzeEvent verification={data} />
			</div>

			<div className="grid gap-4 lg:grid-cols-2">
				<Summary verification={data} />
				<Clip verification={data} />
			</div>
			<div className="grid gap-4 lg:grid-cols-2">
				<Stage1 verification={data} />
				<PriorChain verification={data} />
			</div>
			<Evidence verification={data} />
			<ShadowEvidence verification={data} />
			<BlindPass verification={data} />
			<div className="grid gap-4 lg:grid-cols-2">
				<Outcome verification={data} />
				<VerdictPanel verification={data} />
			</div>
			<TeachCamera verification={data} />
			<RawOutputs verification={data} />
		</div>
	);
};

/**
 * Opens the event this detection belongs to on its reanalysis page, where
 * staff can correct it. Looked up on click: most verifications are never
 * reanalyzed, so resolving the event on every view would be waste.
 */
const ReanalyzeEvent = ({
	verification: v,
}: {
	verification: Verification;
}) => {
	const navigate = useNavigate();
	const find = useFindEventByMeasurement();
	return (
		<div className="flex flex-wrap items-center gap-2">
			{find.error && (
				<span className="text-error-primary text-sm" role="alert">
					{isNotFoundError(find.error)
						? "No event was found for this detection."
						: apiErrorMessage(find.error)}
				</span>
			)}
			<Button
				disabled={find.isPending}
				onClick={() =>
					find.mutate(refId(v.measurement), {
						onSuccess: (event) => void navigate(reanalysisEventPath(event.id)),
					})
				}
				type="button"
				variant="secondary"
			>
				{find.isPending ? "Finding event…" : "Reanalyze this event"}
			</Button>
		</div>
	);
};

const CALL_BASIS = {
	alerted: "confirmed the checked class",
	benign: "Stage 1's own words named this look-alike",
	blind: "the independent blind pass's answer",
} as const;

/**
 * The verifier's conclusion, beside the class it checked. The two differ
 * whenever it rejects the candidate, and a single "Class" row read as though
 * the verifier had said the candidate.
 */
function verifierCallRows(v: Verification) {
	const call = verifierCall(v);
	return call
		? [
				{
					label: "Verifier's call",
					value: `${call.classId} — ${CALL_BASIS[call.basis]}`,
				},
			]
		: [];
}

const Summary = ({ verification: v }: { verification: Verification }) => (
	<DetailPanel title="Summary">
		<KeyValueList
			items={[
				{ label: "Status", value: <Badge value={v.status} /> },
				...(v.skipReason
					? [{ label: "Skip reason", value: v.skipReason }]
					: []),
				...(v.error ? [{ label: "Error", value: v.error }] : []),
				{ label: "Mode", value: v.mode },
				{ label: "Checked class", value: v.classId ?? "—" },
				...verifierCallRows(v),
				...(v.reclassification
					? [
							{
								label: "Re-checked as",
								value: v.reclassification.adopted
									? `The blind pass saw ${v.reclassification.candidateClassId}, so the footage was checked again as that class and it was adopted. First checked as ${v.reclassification.fromClassId}: ${v.reclassification.fromDecision}, ${formatPercent(v.reclassification.fromPosterior)}.`
									: `The blind pass saw ${v.reclassification.candidateClassId}; checked again as that class (${v.reclassification.candidateDecision}, ${formatPercent(v.reclassification.candidatePosterior)}) but kept ${v.reclassification.fromClassId}.`,
							},
						]
					: []),
				...(v.benignResolution
					? [
							{
								label: "Benign classification (legacy)",
								value:
									v.benignResolution.action === "downRanked"
										? `Stage 1's words matched ${v.benignResolution.benignClassId}; down-ranked without a footage review`
										: `Classifier chose ${v.benignResolution.benignClassId}; verified against its alertable neighbour instead`,
							},
						]
					: []),
				{ label: "Decision", value: <Badge value={v.decision} /> },
				{
					label: "Severity",
					value: formatSeverityChange(v.input.severity, v.proposedSeverity),
				},
				{ label: "Posterior", value: formatPercent(v.posterior) },
				{
					label: "Measurement",
					value: <code className="text-xs">{String(v.measurement)}</code>,
				},
				{ label: "Captured", value: formatWhen(v.input.capturedAt) },
				{ label: "Verified", value: formatWhen(v.updatedAt) },
			]}
		/>
	</DetailPanel>
);

/**
 * The clip itself, so the reviewer sees what the verifier saw before
 * answering "what was this really?". The URL is signed per view and expires;
 * a clip the pipeline never stored (a `noMedia` skip) shows as such.
 */
const Clip = ({ verification: v }: { verification: Verification }) => {
	const video = useVerificationVideo(v.input.mediaKey ? v.id : undefined);
	return (
		<ClipPanel
			empty="This verification has no clip attached."
			error={video.error}
			isLoading={video.isLoading}
			mediaKey={v.input.mediaKey}
			testId="verification-clip"
			video={video.data}
		/>
	);
};

/** How the class the footage was checked for was picked, in words. */
function classChoice(v: Verification): string {
	const method = v.canonicalization?.method;
	if (!method) return "—";
	if (method === "model") return "the classifier, from Stage 1's description";
	if (method === "none") {
		return "no class fit: the footage was checked against Stage 1's own description";
	}
	const alias = v.canonicalization?.matchedAlias;
	return `keyword table (legacy)${alias ? `: "${alias}" in ${v.canonicalization?.matchedField}` : ""}`;
}

const Stage1 = ({ verification: v }: { verification: Verification }) => (
	<DetailPanel title="Stage 1 detection">
		<KeyValueList
			items={[
				{
					label: "Type",
					value: <code className="text-xs">{v.input.type}</code>,
				},
				{ label: "Labels", value: v.input.labels.join(", ") || "—" },
				{ label: "Description", value: v.input.description ?? "—" },
				{ label: "Pipeline", value: v.input.pipelineType ?? "anomaly" },
				{
					label: "Class chosen by",
					value: classChoice(v),
				},
			]}
		/>
	</DetailPanel>
);

const PriorChain = ({ verification: v }: { verification: Verification }) => {
	if (!v.prior) return null;
	const chainColumns: Column<
		Verification["prior"] extends infer P
			? P extends { chain: (infer C)[] }
				? C
				: never
			: never
	>[] = [
		{ header: "Scope", key: "scope", render: (c) => c.scopeType },
		{
			header: "Ref",
			key: "ref",
			render: (c) => <code className="text-xs">{c.scopeRef ?? "—"}</code>,
		},
		{ header: "Rows", key: "rows", render: (c) => String(c.candidates) },
		{ header: "Won", key: "won", render: (c) => (c.matched ? "✓" : "") },
	];
	return (
		<DetailPanel title="Prior">
			<KeyValueList
				items={[
					{
						label: "Log odds",
						value: (
							<WithLog log={v.prior.logPriorOdds}>
								{formatProbability(
									probabilityFromLogOdds(v.prior.logPriorOdds),
								)}
							</WithLog>
						),
					},
					{
						label: "Resolved from",
						value: `${v.prior.resolvedFrom} (${v.prior.source})`,
					},
					{
						label: "Local time",
						value: v.localContext
							? `${v.localContext.season}, ${v.localContext.hourBucket} (${v.localContext.timezone}${v.localContext.timezoneMissing ? ", timezone missing" : ""})`
							: "—",
					},
					{ label: "Priors version", value: String(v.prior.tableVersion) },
				]}
			/>
			<div className="mt-3">
				<DataTable
					columns={chainColumns}
					rowKey={(c) => `${c.scopeType}:${c.scopeRef ?? ""}`}
					rows={v.prior.chain}
				/>
			</div>
		</DetailPanel>
	);
};

const Evidence = ({ verification: v }: { verification: Verification }) => {
	if (!v.anchored) return null;
	return (
		<DetailPanel title={`Anchored pass (${v.anchored.model})`}>
			<AnchoredObservations
				contributions={v.likelihood?.perFeature ?? []}
				pass={v.anchored}
			/>
		</DetailPanel>
	);
};

/**
 * The side-by-side model's checklist, feature by feature against the real
 * pass, with the rows where the two disagreed marked. Recorded only; nothing
 * here fed the score.
 */
const ShadowEvidence = ({
	verification: v,
}: {
	verification: Verification;
}) => {
	if (!v.anchoredShadow || !v.anchored) return null;
	const real = new Map(v.anchored.observations.map((o) => [o.feature, o]));
	const rows = v.anchoredShadow.observations.map((o) => {
		const twin = real.get(o.feature);
		return {
			...o,
			differs: twin
				? twin.value !== o.value || twin.strength !== o.strength
				: true,
			realStrength: twin?.strength ?? "—",
			realValue: twin?.value ?? "—",
		};
	});
	type Row = (typeof rows)[number];
	const columns: Column<Row>[] = [
		{
			header: "Feature",
			key: "feature",
			render: (o) => <code className="text-xs">{o.feature}</code>,
		},
		{
			header: `Real (${v.anchored.model})`,
			key: "real",
			render: (o) => `${o.realValue} · ${o.realStrength}`,
		},
		{
			header: `Shadow (${v.anchoredShadow?.model ?? ""})`,
			key: "shadow",
			render: (o) => (
				<span className={o.differs ? "text-warning-primary font-medium" : ""}>
					{o.value} · {o.strength}
				</span>
			),
		},
		{ header: "Shadow note", key: "note", render: (o) => o.note ?? "" },
	];
	const differing = rows.filter((r) => r.differs).length;
	return (
		<DetailPanel
			title={`Anchored pass, side-by-side (${v.anchoredShadow.model}) — not scored`}
		>
			<p className="text-secondary mb-2 text-xs">
				{differing === 0
					? "The shadow model agreed with the real pass on every feature."
					: `${differing} of ${rows.length} features differ from the real pass.`}
			</p>
			<DataTable columns={columns} rowKey={(o) => o.feature} rows={rows} />
		</DetailPanel>
	);
};

const BlindPass = ({ verification: v }: { verification: Verification }) => {
	if (!v.blind) return null;
	return (
		<DetailPanel title={`Blind pass (${v.blind.model})`}>
			{v.blind.ranking ? (
				<ol className="text-primary list-decimal pl-5 text-sm">
					{v.blind.ranking.ranked.map((r) => (
						<li key={r.classId}>
							{r.classId}
							{r.support && (
								<span className="text-tertiary"> — {r.support}</span>
							)}
						</li>
					))}
				</ol>
			) : (
				<p className="text-tertiary text-sm">No usable ranking.</p>
			)}
			<p className="text-secondary mt-2 text-sm">
				Contribution: {formatLogOdds(v.likelihood?.blindContribution)}
			</p>
		</DetailPanel>
	);
};

/**
 * The log leads, in bold and in a fixed-width box so every opening
 * parenthesis starts at the same column. The logs are the figures that add up
 * — prior + evidence = posterior — so they read down the left edge, with the
 * reading a person wants beside each one.
 */
const WithLog = ({
	children,
	log,
}: {
	children: ReactNode;
	log: number | undefined;
}) => (
	<span className="flex items-baseline gap-2">
		<span className="inline-block min-w-12 font-semibold tabular-nums">
			{formatLogOdds(log)}
		</span>
		<span className="text-tertiary">({children})</span>
	</span>
);

const Outcome = ({ verification: v }: { verification: Verification }) => {
	if (v.status !== "completed") return null;
	return (
		<DetailPanel title="Outcome">
			<KeyValueList
				items={[
					{
						label: "Prior",
						value: (
							<WithLog log={v.logPriorOdds}>
								{formatProbability(probabilityFromLogOdds(v.logPriorOdds))}
							</WithLog>
						),
					},
					{
						label: "Evidence",
						value: (
							<WithLog log={v.likelihood?.total}>
								{formatEvidenceFactor(v.likelihood?.total)}
							</WithLog>
						),
					},
					{
						label: "Posterior",
						value: (
							<WithLog log={v.logPosteriorOdds}>
								{formatProbability(v.posterior)}
							</WithLog>
						),
					},
					{
						// The bar is where it is *because* of the cost ratio, so the two
						// belong on one line: FP / (FP + FN) is the whole derivation.
						label: "Alerts at",
						value: `${formatPercent(v.escalationThreshold)}${
							v.costs
								? ` (FN ${v.costs.falseNegative} : FP ${v.costs.falsePositive})`
								: ""
						}`,
					},
					{ label: "Decision", value: <Badge value={v.decision} /> },
					{
						label: "Proposed severity",
						value: String(v.proposedSeverity ?? "—"),
					},
					...(v.appliedSeverity !== undefined
						? [{ label: "Applied severity", value: String(v.appliedSeverity) }]
						: []),
					{
						label: "Versions",
						value: v.versions
							? `prompt ${v.versions.prompt} · taxonomy ${v.versions.taxonomy} · priors ${v.versions.priors} · lr ${v.versions.lr} · specs ${v.versions.discriminators}`
							: "—",
					},
					{
						label: "Total time",
						value: v.timings ? `${v.timings.totalMs} ms` : "—",
					},
				]}
			/>
		</DetailPanel>
	);
};

/**
 * The reviewer's answer. Recorded once per verification; a later answer
 * replaces it. `openedAt` is captured when the panel mounts so time-to-verdict
 * measures how long the evidence took to read, not how long the tab sat open
 * before anyone looked.
 */
const VerdictPanel = ({ verification: v }: { verification: Verification }) => {
	const taxonomy = useTaxonomy();
	const record = useRecordVerdict(v.id);
	const suggest = useSuggestVerdictClass(v.id);
	const [openedAt] = useState(() => new Date().toISOString());
	// What the verifier concluded, not the class it checked: pre-selected so
	// confirming it is one click, and labelled as a default so it is not
	// mistaken for the reviewer's own answer.
	const call = verifierCall(v);
	// Seeded from the stored verdict so "Replace verdict" starts from what was
	// written rather than blanking it.
	const [trueClassId, setTrueClassId] = useState(
		v.verdict?.trueClassId ?? call?.classId ?? "",
	);
	const [note, setNote] = useState(v.verdict?.note ?? "");
	const [suggestion, setSuggestion] = useState<null | VerdictSuggestion>(null);

	if (v.status !== "completed") return null;
	const classes = taxonomy.data ?? [];

	// Only report the suggestion alongside the verdict while the reviewer has
	// kept it; changing the dropdown means they overrode it, and recording it
	// as accepted would overstate the classifier.
	const kept = suggestion && suggestion.classId === trueClassId;
	const prefilled =
		!v.verdict &&
		!suggestion &&
		call !== undefined &&
		trueClassId === call.classId;

	return (
		<DetailPanel title="Verdict">
			{v.verdict && (
				<div className="text-secondary mb-3 text-sm">
					<p>
						Recorded {formatWhen(v.verdict.verdictAt)}:{" "}
						<span className="text-primary font-medium">
							{v.verdict.trueClassId}
						</span>
						{call &&
							(v.verdict.trueClassId === call.classId
								? " (agrees with the verifier's call)"
								: ` (differs from the verifier's call: ${call.classId})`)}
					</p>
					{v.verdict.note && (
						<p className="text-primary mt-1 italic">“{v.verdict.note}”</p>
					)}
				</div>
			)}
			<form
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					if (!trueClassId) return;
					record.mutate({
						note: note.trim() || undefined,
						openedAt,
						suggestedClassId: kept ? suggestion.classId : undefined,
						suggestionMethod: kept ? suggestion.method : undefined,
						trueClassId,
					});
				}}
			>
				<Field
					htmlFor="verdict-note"
					label="What happened, or what did it get wrong?"
				>
					<TextArea
						id="verdict-note"
						onChange={(e) => setNote(e.target.value)}
						placeholder="That's not a bear, it's a dog."
						rows={3}
						value={note}
					/>
				</Field>
				<div className="flex flex-wrap items-end gap-3">
					<Button
						disabled={suggest.isPending || !note.trim()}
						onClick={() => {
							suggest.mutate(note.trim(), {
								// A failed call answered nothing, so drop the previous
								// suggestion rather than leaving it beside the error where
								// it reads as a reply to what they just typed.
								onError: () => setSuggestion(null),
								onSuccess: (result) => {
									setSuggestion(result);
									setTrueClassId(result.classId);
								},
							});
						}}
						type="button"
						variant="secondary"
					>
						{suggest.isPending ? "Reading…" : "Suggest class"}
					</Button>
					<Field htmlFor="verdict-class" label="What was this really?">
						<Select
							id="verdict-class"
							onChange={(e) => setTrueClassId(e.target.value)}
							value={trueClassId}
						>
							<option value="">Choose…</option>
							<option value="none">none — nothing alertable</option>
							{classes.map((cls) => (
								<option key={cls.classId} value={cls.classId}>
									{cls.classId}
								</option>
							))}
						</Select>
					</Field>
					<Button disabled={record.isPending || !trueClassId} type="submit">
						{v.verdict ? "Replace verdict" : "Record verdict"}
					</Button>
				</div>
				{prefilled && (
					<p className="text-secondary text-sm">
						Pre-filled from the verifier's call — change it if that's wrong.
					</p>
				)}
				{suggestion && (
					<p className="text-secondary text-sm">
						{suggestion.method === "none"
							? "That doesn't match any class we have. Recorded as none."
							: `Suggested ${suggestion.classId} — chosen by the classifier. Change it below if that's wrong.`}
					</p>
				)}
				{suggest.error && (
					<span className="text-error-primary text-sm" role="alert">
						{suggest.error.message}
					</span>
				)}
				{record.error && (
					<span className="text-error-primary text-sm" role="alert">
						{record.error.message}
					</span>
				)}
				{record.isSuccess && (
					<span className="text-secondary text-sm">Saved.</span>
				)}
			</form>
		</DetailPanel>
	);
};

/**
 * Teach the camera something about its scene from this clip — where the
 * exits are, what lies past the edge of the frame. The note only queues a
 * draft: a model turns it into descriptive scene facts, and nothing reaches
 * the camera until staff apply the proposal on its own page, which this
 * opens. A clip is required, since the draft reads it.
 */
const TeachCamera = ({ verification: v }: { verification: Verification }) => {
	const navigate = useNavigate();
	const create = useCreateSceneProposal();
	const [note, setNote] = useState("");

	if (!v.input.mediaKey) return null;

	return (
		<DetailPanel title="Teach this camera">
			<p className="text-secondary mb-3 text-sm">
				Did the model misread the place itself? Say what it could not know from
				the frame, in plain words. You will review the drafted scene facts
				before anything changes.
			</p>
			<form
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					if (!note.trim()) return;
					create.mutate(
						{ note: note.trim(), verificationId: v.id },
						{
							onSuccess: (proposal) =>
								void navigate(sceneProposalPath(proposal.id)),
						},
					);
				}}
			>
				<Field
					htmlFor="teach-note"
					label="What should this camera know about its scene?"
				>
					<TextArea
						id="teach-note"
						onChange={(e) => setNote(e.target.value)}
						placeholder="These people are still shopping. The bottom of the frame is more store; the only door out is top right."
						rows={3}
						value={note}
					/>
				</Field>
				<div>
					<Button disabled={create.isPending || !note.trim()} type="submit">
						{create.isPending ? "Queuing…" : "Draft scene update"}
					</Button>
				</div>
				{create.error && (
					<p className="text-error-primary text-sm" role="alert">
						{apiErrorMessage(create.error)}
					</p>
				)}
			</form>
		</DetailPanel>
	);
};

const RawOutputs = ({ verification: v }: { verification: Verification }) => (
	<div className="flex flex-col gap-2">
		{v.anchored && (
			<JsonBlock label="Raw anchored reply" value={v.anchored.raw} />
		)}
		{v.blind && <JsonBlock label="Raw blind reply" value={v.blind.raw} />}
		{v.canonicalization?.modelRaw !== undefined && (
			<JsonBlock
				label="Raw classifier reply"
				value={v.canonicalization.modelRaw}
			/>
		)}
		<JsonBlock label="Full record" value={v} />
	</div>
);
